import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Lock, ArrowRight, Truck } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatINR } from "../contexts/CartContext";
import { createOrder, fetchPromoCode, fetchShippingRate, countPromoUsageByEmail, getSetting, lookupPincode, fetchActivePromoCodes, payWithRazorpay } from "../lib/api";

const STEPS = ["Delivery", "Payment", "Confirmation"];

export default function Checkout() {
  const { items, subtotal, count, clear } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [paying, setPaying] = useState(false);
  const [delivery, setDelivery] = useState({
    full_name: "", phone: "", email: "",
    address1: "", address2: "",
    city: "", state: "", pin: "",
  });
  const [order, setOrder] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // { code, type, value, label, waives_shipping }
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [shippingFee, setShippingFee] = useState(null);
  const [freeShipThreshold, setFreeShipThreshold] = useState(0);
  const [checkoutCopy, setCheckoutCopy] = useState("");
  const [activeCodes, setActiveCodes] = useState([]);

  // Load the list of currently active promo codes so the hint is never stale
  useEffect(() => {
    let cancelled = false;
    fetchActivePromoCodes().then((codes) => { if (!cancelled) setActiveCodes(codes); });
    return () => { cancelled = true; };
  }, []);

  // Load configurable settings once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [threshold, copy] = await Promise.all([
        getSetting("free_shipping_threshold", "0"),
        getSetting("shipping_copy_checkout", "Shipping calculated by location"),
      ]);
      if (cancelled) return;
      setFreeShipThreshold(Number(threshold) || 0);
      setCheckoutCopy(copy);
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-fill city and state from the PIN code as soon as 6 digits are entered
  useEffect(() => {
    const pin = delivery.pin?.trim();
    if (!/^\d{6}$/.test(pin)) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const res = await lookupPincode(pin);
      if (cancelled || !res) return;
      setDelivery((d) => ({
        ...d,
        state: res.state || d.state,
        city: res.city || d.city,
      }));
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [delivery.pin]);

  // Recalculate the shipping fee whenever the delivery state changes
  useEffect(() => {
    let cancelled = false;
    const state = delivery.state?.trim();
    if (!state) { setShippingFee(null); return; }
    const t = setTimeout(async () => {
      const rate = await fetchShippingRate(state);
      if (!cancelled) setShippingFee(rate);
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [delivery.state]);

  const applyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) { setPromoError("Enter a promo code"); return; }
    setPromoLoading(true);
    setPromoError("");
    const found = await fetchPromoCode(code);
    if (!found) { setPromoLoading(false); setPromoError("Invalid or expired promo code"); setPromoApplied(null); return; }
    if (found.min_order && subtotal < found.min_order) {
      setPromoLoading(false);
      setPromoError(`Minimum order ₹${found.min_order} for this code`);
      setPromoApplied(null);
      return;
    }
    if (found.min_quantity && count < found.min_quantity) {
      setPromoLoading(false);
      setPromoError(`Add ${found.min_quantity} or more items to use this code`);
      setPromoApplied(null);
      return;
    }
    // Per-customer usage limit — requires an email to check against
    if (found.max_per_customer) {
      if (!delivery.email) {
        setPromoLoading(false);
        setPromoError("Enter your email above first, then apply this code");
        setPromoApplied(null);
        return;
      }
      const used = await countPromoUsageByEmail(delivery.email, found.code);
      if (used >= found.max_per_customer) {
        setPromoLoading(false);
        setPromoError(`This code can only be used ${found.max_per_customer} times per customer`);
        setPromoApplied(null);
        return;
      }
    }
    setPromoLoading(false);
    setPromoApplied(found);
    setPromoError("");
    toast.success(`Promo applied: ${found.label}`);
  };

  const promoDiscount = useMemo(() => {
    if (!promoApplied) return 0;
    if (promoApplied.type === "percent") return Math.round(subtotal * promoApplied.value / 100);
    if (promoApplied.type === "flat") return Math.min(promoApplied.value, subtotal);
    // Buy X Get Y — cheapest qualifying items are free.
    // Configure by setting type='bxgy', buy_qty and get_qty on the promo code.
    if (promoApplied.type === "bxgy") {
      const buyQty = promoApplied.buy_qty || 0;
      const getQty = promoApplied.get_qty || 0;
      if (buyQty <= 0 || getQty <= 0) return 0;
      // Expand cart into a flat list of unit prices, cheapest first
      const units = [];
      items.forEach((i) => { for (let n = 0; n < i.qty; n++) units.push(i.price); });
      units.sort((a, b) => a - b);
      const groupSize = buyQty + getQty;
      const freeCount = Math.floor(units.length / groupSize) * getQty;
      return units.slice(0, freeCount).reduce((sum, p) => sum + p, 0);
    }
    return 0;
  }, [promoApplied, subtotal, items]);

  // Shipping is free if a promo waives it, or if the configurable spend
  // threshold is enabled (> 0) and the subtotal meets it.
  const qualifiesFreeShipping =
    !!promoApplied?.waives_shipping ||
    (freeShipThreshold > 0 && subtotal >= freeShipThreshold);

  const shippingKnown = shippingFee !== null;

  const deliveryFee = qualifiesFreeShipping || !shippingKnown ? 0 : shippingFee;

  const total = subtotal - promoDiscount + deliveryFee;

  const isDeliveryValid = () => {
    const { full_name, phone, email, address1, city, state, pin } = delivery;
    return full_name && phone && email && address1 && city && state && pin && /^\d{6}$/.test(pin);
  };

  const onContinueToPayment = (e) => {
    e.preventDefault();
    if (!isDeliveryValid()) { toast.error("Please fill all delivery details (PIN must be 6 digits)."); return; }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onPay = async () => {
    if (items.length === 0) { toast.error("Cart is empty."); return; }
    // Block any sold-out ("Threaded Out") items from payment
    const soldOut = items.filter((i) => i.availabilityState === "sold_out");
    if (soldOut.length > 0) {
      toast.error(`${soldOut[0].name} is Threaded Out and can't be purchased yet. Remove it to continue.`, { duration: 3500 });
      return;
    }
    setPaying(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty, size: i.size || null })),
        subtotal,
        promo_code: promoApplied?.code || null,
        promo_discount: promoDiscount,
        delivery_fee: deliveryFee,
        total,
        delivery,
        payment_method: "razorpay",
      };
      const created = await createOrder(payload);

      // Open Razorpay and wait for verified payment
      try {
        await payWithRazorpay({
          amountINR: total,
          orderNumber: created.order_number,
          customer: { name: delivery?.name, email: delivery?.email, phone: delivery?.phone },
        });
      } catch (payErr) {
        // Payment cancelled / failed / not configured — keep order pending, inform user
        setPaying(false);
        toast.error(payErr.message || "Payment was not completed.");
        return;
      }

      // Payment verified — persist to local order history
      try {
        const key = "mt_my_orders";
        const existing = JSON.parse(window.localStorage.getItem(key) || "[]");
        const next = [created.order_number, ...existing.filter((n) => n !== created.order_number)].slice(0, 20);
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch (_) { /* ignore storage errors */ }
      setOrder(created);
      clear();
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_) {
      toast.error("Could not place order. Try again.");
    } finally {
      setPaying(false);
    }
  };

  // Guard: if cart empty and not in confirmation, block
  if (items.length === 0 && step !== 3) {
    return (
      <section className="mt-section">
        <div className="mt-container text-center max-w-lg mx-auto">
          <h1 className="font-display font-extralight text-[clamp(2rem,4vw,3rem)]">Your cart is empty.</h1>
          <p className="mt-3 mt-italic-silver">Every thread has a story. Start yours.</p>
          <Link to="/shop" className="mt-btn mt-rainbow-edge mt-8 inline-flex" data-testid="empty-cart-shop">Browse the Edit</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="page-first-section" data-testid="checkout-section" style={{paddingBottom:"clamp(2.5rem,6vw,6rem)"}}>
      <div className="mt-container">
        {/* Stepper */}
        <div className="mt-glass rounded-2xl px-3 sm:px-6 py-6 mb-6 overflow-hidden" data-testid="checkout-stepper">
          <div className="flex items-center gap-1.5 sm:gap-4">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const isDone = step > n;
              const isActive = step === n;
              return (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                    <span className={`mt-step-dot ${isActive ? "is-active" : isDone ? "is-done" : ""}`} aria-hidden="true">
                      {isDone && <Check size={10} className="block mx-auto" color="#0a0a0a" />}
                    </span>
                    <span className="font-mono text-[0.56rem] sm:text-[0.72rem] tracking-mt uppercase whitespace-nowrap" style={{ color: isActive ? "var(--mt-text)" : isDone ? "var(--mt-text)" : "var(--mt-text-muted)", fontWeight: isActive ? 700 : 500, opacity: isActive || isDone ? 1 : 0.6 }}>
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && <span className={`mt-step-line ${step > n ? "is-filled" : ""}`} aria-hidden="true" style={{ minWidth: "8px" }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {step !== 3 && (
          <div className="mt-glass rounded-2xl px-6 py-4 mb-10 flex items-center gap-3" data-testid="checkout-delivery-estimate">
            <Truck size={15} style={{ color: "var(--mt-text-muted)" }} />
            <p className="font-mono text-[0.62rem] sm:text-[0.66rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
              Delivery tracking shared via email once your order ships
            </p>
          </div>
        )}

        {/* Step 3 — Confirmation (full width) */}
        {step === 3 && order && (
          <Confirmation order={order} />
        )}

        {/* Step 1 & 2 */}
        {step !== 3 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              {step === 1 && (
                <form onSubmit={onContinueToPayment} className="mt-glass mt-rainbow-edge rounded-[22px] p-7 sm:p-9" data-testid="delivery-form">
                  <h2 className="font-display text-3xl font-extralight">Delivery Details</h2>
                  <p className="mt-italic-silver mt-1 text-sm">Every thread has a story. Let&rsquo;s get yours home.</p>

                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Full Name"><input className="mt-input" value={delivery.full_name} onChange={(e) => setDelivery({ ...delivery, full_name: e.target.value })} required name="name" autoComplete="name" placeholder="Full name" data-testid="del-name" /></Field>
                    <Field label="Phone"><input className="mt-input" type="tel" value={delivery.phone} onChange={(e) => setDelivery({ ...delivery, phone: e.target.value })} required name="tel" autoComplete="tel" placeholder="Phone number" data-testid="del-phone" /></Field>
                    <Field label="Email" className="sm:col-span-2"><input className="mt-input" type="email" value={delivery.email} onChange={(e) => setDelivery({ ...delivery, email: e.target.value })} required name="email" autoComplete="email" placeholder="you@email.com" data-testid="del-email" /></Field>
                    <Field label="Address Line 1" className="sm:col-span-2"><input className="mt-input" value={delivery.address1} onChange={(e) => setDelivery({ ...delivery, address1: e.target.value })} required name="address-line1" autoComplete="address-line1" placeholder="Street address" data-testid="del-addr1" /></Field>
                    <Field label="Address Line 2 (Optional)" className="sm:col-span-2"><input className="mt-input" value={delivery.address2} onChange={(e) => setDelivery({ ...delivery, address2: e.target.value })} name="address-line2" autoComplete="address-line2" placeholder="Apartment, suite, etc. (optional)" data-testid="del-addr2" /></Field>
                    <Field label="City"><input className="mt-input" value={delivery.city} onChange={(e) => setDelivery({ ...delivery, city: e.target.value })} required name="city" autoComplete="address-level2" placeholder="City" data-testid="del-city" /></Field>
                    <Field label="State"><input className="mt-input" value={delivery.state} onChange={(e) => setDelivery({ ...delivery, state: e.target.value })} required name="state" autoComplete="address-level1" placeholder="State" data-testid="del-state" /></Field>
                    <Field label="PIN Code" className="sm:col-span-2"><input className="mt-input" maxLength={6} value={delivery.pin} onChange={(e) => setDelivery({ ...delivery, pin: e.target.value.replace(/\D/g, "") })} required name="postal-code" autoComplete="postal-code" placeholder="PIN code" data-testid="del-pin" /></Field>
                  </div>

                  <button type="submit" className="mt-btn mt-rainbow-edge w-full mt-8" data-testid="continue-to-payment">
                    Continue to Payment <ArrowRight size={14} />
                  </button>
                </form>
              )}

              {step === 2 && (
                <div className="mt-glass mt-rainbow-edge rounded-[22px] p-7 sm:p-9" data-testid="payment-form">
                  <h2 className="font-display text-3xl font-extralight">Payment</h2>
                  <p className="mt-3 font-body text-sm" style={{ color: "var(--mt-text-muted)" }}>
                    You'll be redirected to Razorpay's secure payment page to complete your order. Once paid, you'll be brought back here automatically.
                  </p>

                  <div className="mt-7 mt-glass rounded-2xl p-5 flex items-center gap-4" style={{ border: "1px solid var(--mt-glass-border)" }}>
                    <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(0,170,255,0.12)" }}>
                      <Lock size={16} style={{ color: "#00AAFF" }} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-medium">Secured by Razorpay</p>
                      <p className="font-mono text-[0.58rem] tracking-mt uppercase opacity-60 mt-0.5">UPI · Cards · Net Banking · Wallets</p>
                    </div>
                  </div>

                  <button type="button" onClick={onPay} disabled={paying} className="mt-btn mt-btn-blood w-full mt-8" data-testid="pay-now">
                    {paying ? "Processing…" : <>Pay {formatINR(total)} with Razorpay <ArrowRight size={14} /></>}
                  </button>
                  <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[0.6rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                    <Lock size={10} /> Your details are never shared
                  </p>
                  <button type="button" onClick={() => setStep(1)} className="block mx-auto mt-3 font-mono text-[0.6rem] tracking-mt uppercase opacity-60 hover:opacity-100" data-testid="back-to-delivery">
                    ← Back to Delivery
                  </button>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <aside className="lg:col-span-5">
              <div className="mt-glass mt-rainbow-edge rounded-[22px] p-7 lg:sticky lg:top-28" data-testid="order-summary">
                <h3 className="font-display text-2xl font-extralight">Order Summary</h3>
                <div className="mt-5 space-y-4 max-h-[360px] overflow-y-auto pr-2">
                  {items.map((it) => (
                    <div key={it.id + (it.size || "")} className="flex items-center gap-4">
                      <div className="w-14 h-16 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: "linear-gradient(140deg, #1a1a1e, #000)" }}>
                        {it.image ? (
                          <img src={it.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-display font-semibold" style={{ color: "rgba(245,245,245,0.3)" }}>MT</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-sm truncate">{it.name}</p>
                        <p className="font-mono text-[0.55rem] tracking-mt uppercase opacity-60">Qty {it.qty}{it.size ? ` · ${it.size}` : ""}</p>
                      </div>
                      <div className="font-mono text-xs font-bold">{formatINR(it.qty * it.price)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-2 font-mono text-xs">
                  <Row k="Subtotal" v={formatINR(subtotal)} />
                  {promoApplied && promoDiscount > 0 && (
                    <div className="flex items-center justify-between" data-testid="promo-discount-row">
                      <span className="flex items-center gap-1.5" style={{ color: "#00AAFF" }}>
                        <span>Promo ({promoApplied.code})</span>
                        <button onClick={() => { setPromoApplied(null); setPromoCode(""); }} className="opacity-60 hover:opacity-100 text-[0.55rem]" aria-label="Remove promo">✕</button>
                      </span>
                      <span style={{ color: "#00AAFF" }}>−{formatINR(promoDiscount)}</span>
                    </div>
                  )}
                  {qualifiesFreeShipping && (
                    <div className="flex items-center justify-between" data-testid="promo-shipping-row">
                      <span style={{ color: "#00AAFF" }}>
                        Free Shipping{promoApplied?.waives_shipping ? ` (${promoApplied.code})` : ""}
                      </span>
                      <span style={{ color: "#00AAFF" }}>Applied</span>
                    </div>
                  )}
                  <Row k="Delivery" v={!shippingKnown && !qualifiesFreeShipping ? "—" : (deliveryFee === 0 ? "Free" : formatINR(deliveryFee))} />
                </div>

                {/* Promo code input */}
                <div className="mt-4" data-testid="promo-section">
                  <p className="font-mono text-[0.6rem] tracking-mt uppercase opacity-60 mb-2">Promo / Discount Code</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                      placeholder="Enter code"
                      className="mt-input flex-1 text-[0.78rem] py-2"
                      style={{ fontSize: "0.78rem", padding: "0.5rem 0.8rem", letterSpacing: "0.12em" }}
                      disabled={!!promoApplied}
                      data-testid="promo-input"
                    />
                    <button
                      type="button"
                      onClick={promoApplied ? () => { setPromoApplied(null); setPromoCode(""); } : applyPromo}
                      className="mt-btn px-4 py-2 text-[0.65rem]"
                      style={{ whiteSpace: "nowrap" }}
                      data-testid="promo-apply-btn"
                    >
                      {promoApplied ? "Remove" : (promoLoading ? "..." : "Apply")}
                    </button>
                  </div>
                  {promoError && <p className="mt-1 font-mono text-[0.58rem] tracking-mt" style={{ color: "#8B0000" }} data-testid="promo-error">{promoError}</p>}
                  {promoApplied && <p className="mt-1 font-mono text-[0.58rem] tracking-mt" style={{ color: "#00AAFF" }} data-testid="promo-success">✓ {promoApplied.label} applied</p>}
                  {activeCodes.length > 0 && (
                    <p className="mt-2 font-mono text-[0.5rem] tracking-mt uppercase opacity-40">Try: {activeCodes.join(" · ")}</p>
                  )}
                </div>
                <span className="mt-blood-line wide mt-5 mb-3" aria-hidden="true" />
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-[0.7rem] tracking-mt uppercase">Total</span>
                  <span className="font-display text-3xl font-medium" data-testid="order-total">{formatINR(total)}</span>
                </div>
                <p className="mt-4 flex items-center gap-2 font-mono text-[0.58rem] tracking-mt uppercase opacity-60">
                  <Truck size={10} /> {checkoutCopy}{freeShipThreshold > 0 ? ` — free above ${formatINR(freeShipThreshold)}` : ""}
                </p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[0.62rem] tracking-mt uppercase mb-2 block" style={{ color: "var(--mt-text)" }}>{label}</span>
      {children}
    </label>
  );
}
function Row({ k, v }) {
  return <div className="flex items-baseline justify-between"><span className="opacity-60 tracking-mt uppercase text-[0.6rem]">{k}</span><span>{v}</span></div>;
}
function Confirmation({ order }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-xl mx-auto mt-glass mt-rainbow-edge rounded-[22px] p-10 text-center" data-testid="order-confirmation">
      <svg className="mx-auto mt-keep-anim" width="88" height="88" viewBox="0 0 88 88" fill="none">
        <circle cx="44" cy="44" r="40" stroke="#00AAFF" strokeWidth="2" className="mt-checkmark-circle" style={{ transformOrigin: "center", transform: "rotate(-90deg)" }} />
        <path d="M26 46 L40 60 L64 32" stroke="#00AAFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" className="mt-checkmark" />
      </svg>
      <h1 className="mt-6 font-display font-extralight text-[clamp(2rem,5vw,3.6rem)]">
        Order <span className="italic mt-chrome-text mt-keep-anim">Confirmed.</span>
      </h1>
      <p className="mt-italic-silver mt-3">Your order has been placed successfully. A confirmation is on its way to your email.</p>
      <p className="mt-5 font-mono text-sm tracking-mt uppercase mt-blood" data-testid="order-number" style={{ fontWeight: 700 }}>
        {order.order_number}
      </p>
      <div className="mt-6 text-left mt-glass rounded-2xl p-5 font-mono text-xs space-y-2">
        {order.items.map((it, i) => (
          <div key={i} className="flex items-baseline justify-between">
            <span className="truncate pr-4">{it.name}{it.size ? ` · ${it.size}` : ""} × {it.qty}</span>
            <span className="font-bold">{formatINR(it.qty * it.price)}</span>
          </div>
        ))}
        <div className="h-px my-2" style={{ background: "var(--mt-glass-border)" }} />
        <div className="flex items-baseline justify-between">
          <span className="opacity-60 tracking-mt uppercase text-[0.6rem]">Total</span>
          <span className="font-display text-xl font-medium">{formatINR(order.total)}</span>
        </div>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/shop" className="mt-btn" data-testid="continue-shopping">Continue Shopping</Link>
        <button type="button" onClick={() => navigate("/profile")} className="mt-btn mt-btn-blue" data-testid="track-order">Track Order</button>
      </div>
    </div>
  );
}
